import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Calendar, Clock, Plus, Edit2, Trash2, Users, AlertTriangle, Youtube, ChevronLeft, ChevronRight, RefreshCw, Eye, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBimtalks = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSubscribersModal, setShowSubscribersModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [liveLink, setLiveLink] = useState('');
  const [liveMessage, setLiveMessage] = useState('');
  const [sendingLink, setSendingLink] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [includePast, setIncludePast] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    guest_name: '',
    guest_role: '',
    date: '',
    time: '10:00',
    description: '',
    youtube_link: '',
    is_special_time: false
  });

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API}/bimtalks/admin/events?include_past=${includePast}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEvents(data);
      }
    } catch (error) {
      toast.error('Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [token, includePast]);

  const handleSeed2026 = async () => {
    setSeeding(true);
    try {
      const res = await fetch(`${API}/bimtalks/admin/seed-2026`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchEvents();
      } else {
        toast.error(data.detail || 'Erro ao criar eventos');
      }
    } catch (error) {
      toast.error('Erro ao criar eventos');
    } finally {
      setSeeding(false);
    }
  };

  const openCreateModal = () => {
    // Default to next Saturday at 10:00
    const today = new Date();
    const daysUntilSaturday = (6 - today.getDay() + 7) % 7 || 7;
    const nextSaturday = new Date(today);
    nextSaturday.setDate(today.getDate() + daysUntilSaturday);
    
    setFormData({
      title: 'BIMTALKS Live',
      guest_name: '',
      guest_role: '',
      date: nextSaturday.toISOString().split('T')[0],
      time: '10:00',
      description: '',
      youtube_link: '',
      is_special_time: false
    });
    setSelectedEvent(null);
    setShowModal(true);
  };

  const openEditModal = (event) => {
    const eventDate = new Date(event.date);
    setFormData({
      title: event.title,
      guest_name: event.guest_name,
      guest_role: event.guest_role || '',
      date: eventDate.toISOString().split('T')[0],
      time: eventDate.toTimeString().slice(0, 5),
      description: event.description || '',
      youtube_link: event.youtube_link || '',
      is_special_time: event.is_special_time
    });
    setSelectedEvent(event);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const dateTime = new Date(`${formData.date}T${formData.time}:00`);
    
    const payload = {
      title: formData.title,
      guest_name: formData.guest_name,
      guest_role: formData.guest_role || null,
      date: dateTime.toISOString(),
      description: formData.description || null,
      youtube_link: formData.youtube_link || null,
      is_special_time: formData.is_special_time
    };

    try {
      let res;
      if (selectedEvent) {
        res = await fetch(`${API}/bimtalks/admin/events/${selectedEvent.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${API}/bimtalks/admin/events`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      }

      if (res.ok) {
        toast.success(selectedEvent ? 'Evento atualizado!' : 'Evento criado!');
        setShowModal(false);
        fetchEvents();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erro ao salvar evento');
      }
    } catch (error) {
      toast.error('Erro ao salvar evento');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Tem certeza que deseja excluir este evento?')) return;
    
    try {
      const res = await fetch(`${API}/bimtalks/admin/events/${eventId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Evento excluído!');
        fetchEvents();
      } else {
        toast.error('Erro ao excluir evento');
      }
    } catch (error) {
      toast.error('Erro ao excluir evento');
    }
  };

  const viewSubscribers = async (event) => {
    setSelectedEvent(event);
    setLiveLink(event.youtube_link || '');
    setLiveMessage('');
    try {
      const res = await fetch(`${API}/bimtalks/admin/events/${event.id}/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSubscribers(data);
        setShowSubscribersModal(true);
      }
    } catch (error) {
      toast.error('Erro ao carregar inscritos');
    }
  };

  const handleSendLink = async () => {
    if (!liveLink.trim()) {
      toast.error('Informe o link de acesso da live');
      return;
    }
    if (!window.confirm(`Enviar o link para ${subscribers.length} inscrito(s)?`)) return;
    setSendingLink(true);
    try {
      const res = await fetch(`${API}/bimtalks/admin/events/${selectedEvent.id}/send-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ link: liveLink.trim(), message: liveMessage.trim() || null })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Link enviado!');
      } else {
        toast.error(data.detail || 'Erro ao enviar o link');
      }
    } catch (error) {
      toast.error('Erro ao enviar o link');
    } finally {
      setSendingLink(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'short',
      day: '2-digit', 
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center">
            <Youtube className="mr-2 text-red-500" size={24} />
            Agenda BIMTALKS
          </CardTitle>
          <div className="flex gap-2">
            <Button
              onClick={handleSeed2026}
              disabled={seeding}
              variant="outline"
              className="border-zinc-700 text-white"
            >
              {seeding ? (
                <RefreshCw className="mr-2 animate-spin" size={18} />
              ) : (
                <Calendar className="mr-2" size={18} />
              )}
              Gerar Quintas 2026
            </Button>
            <Button
              onClick={openCreateModal}
              className="bg-red-600 hover:bg-red-700"
            >
              <Plus className="mr-2" size={18} />
              Novo Evento
            </Button>
          </div>
        </div>
        
        <div className="flex items-center mt-4">
          <label className="flex items-center text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={includePast}
              onChange={(e) => setIncludePast(e.target.checked)}
              className="mr-2"
            />
            Mostrar eventos passados
          </label>
        </div>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-400">Carregando...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Nenhum evento encontrado</p>
            <p className="text-gray-500 text-sm mt-2">
              Clique em "Gerar Quintas 2026" para criar a agenda completa
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {events.map((event) => {
              const eventDate = new Date(event.date);
              const isPast = eventDate < new Date();
              
              return (
                <div
                  key={event.id}
                  className={`bg-zinc-800 border rounded-lg p-4 ${
                    isPast ? 'border-zinc-700 opacity-60' : 'border-zinc-700'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="bg-red-600/20 rounded-lg p-2 text-center min-w-[60px]">
                        <div className="text-lg font-bold text-red-400">
                          {eventDate.getDate()}
                        </div>
                        <div className="text-xs text-gray-400">
                          {eventDate.toLocaleDateString('pt-BR', { month: 'short' })}
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-white font-semibold">{event.title}</h3>
                          {event.is_special_time && (
                            <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-500/30 text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Especial
                            </Badge>
                          )}
                          {!event.is_active && (
                            <Badge className="bg-gray-600/20 text-gray-400 border-gray-500/30 text-xs">
                              Inativo
                            </Badge>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm">
                          <span className="text-red-400">{event.guest_name}</span>
                          {event.guest_role && <span className="text-gray-500"> • {event.guest_role}</span>}
                        </p>
                        <p className="text-gray-500 text-xs mt-1">
                          {formatDate(event.date)} às {formatTime(event.date)}h
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => viewSubscribers(event)}
                        variant="ghost"
                        size="sm"
                        className="text-gray-400 hover:text-white"
                      >
                        <Users className="w-4 h-4 mr-1" />
                        {event.subscribers_count}
                      </Button>
                      <Button
                        onClick={() => openEditModal(event)}
                        variant="ghost"
                        size="sm"
                        className="text-gray-400 hover:text-blue-400"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(event.id)}
                        variant="ghost"
                        size="sm"
                        className="text-gray-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-white mb-6">
              {selectedEvent ? 'Editar Evento' : 'Novo Evento'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Título *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(f => ({ ...f, title: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="BIMTALKS Live"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Convidado *</label>
                  <Input
                    value={formData.guest_name}
                    onChange={(e) => setFormData(f => ({ ...f, guest_name: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="Nome do convidado"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Cargo/Especialidade</label>
                  <Input
                    value={formData.guest_role}
                    onChange={(e) => setFormData(f => ({ ...f, guest_role: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="Ex: Arquiteto BIM"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Data *</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(f => ({ ...f, date: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Horário *</label>
                  <Input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData(f => ({ ...f, time: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md p-2 min-h-[80px]"
                  placeholder="Descrição do evento..."
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Link do YouTube (após a live)</label>
                <Input
                  value={formData.youtube_link}
                  onChange={(e) => setFormData(f => ({ ...f, youtube_link: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="special_time"
                  checked={formData.is_special_time}
                  onChange={(e) => setFormData(f => ({ ...f, is_special_time: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="special_time" className="text-gray-400 text-sm">
                  Marcar como horário especial (exibe aviso aos visitantes)
                </label>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowModal(false)}
                  variant="outline"
                  className="flex-1 border-zinc-700"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {selectedEvent ? 'Salvar' : 'Criar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subscribers Modal */}
      {showSubscribersModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                <Users className="inline mr-2" />
                Inscritos - {selectedEvent?.title}
              </h2>
              <Button
                onClick={() => setShowSubscribersModal(false)}
                variant="ghost"
                size="sm"
              >
                ✕
              </Button>
            </div>
            
            {subscribers.length === 0 ? (
              <p className="text-gray-400 text-center py-8">
                Nenhum inscrito ainda
              </p>
            ) : (
              <div className="space-y-2">
                {subscribers.map((sub) => (
                  <div
                    key={sub.id}
                    className="bg-zinc-800 rounded-lg p-3 flex items-center justify-between"
                  >
                    <div>
                      <p className="text-white font-medium">{sub.name}</p>
                      <p className="text-gray-400 text-sm">{sub.email}</p>
                      {sub.whatsapp && (
                        <p className="text-gray-500 text-xs">{sub.whatsapp}</p>
                      )}
                    </div>
                    <span className="text-gray-500 text-xs">
                      {new Date(sub.subscribed_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-6 text-center">
              <p className="text-gray-400 text-sm">
                Total: {subscribers.length} inscritos
              </p>
            </div>

            {/* Enviar link da live a todos os inscritos */}
            <div className="mt-6 pt-6 border-t border-zinc-700">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Send size={16} className="text-red-500" />
                Enviar link a todos os inscritos
              </h3>
              <input
                type="url"
                value={liveLink}
                onChange={(e) => setLiveLink(e.target.value)}
                placeholder="https://... (Zoom, YouTube privado, Meet, etc.)"
                className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm mb-2 focus:outline-none focus:border-red-500"
                data-testid="live-link-input"
              />
              <textarea
                value={liveMessage}
                onChange={(e) => setLiveMessage(e.target.value)}
                placeholder="Mensagem opcional (ex: a live começa às 20h, entre 5 min antes)"
                rows={2}
                className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm mb-3 focus:outline-none focus:border-red-500"
                data-testid="live-message-input"
              />
              <Button
                onClick={handleSendLink}
                disabled={sendingLink || subscribers.length === 0}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                data-testid="send-live-link-btn"
              >
                {sendingLink ? 'Enviando...' : `Enviar link para ${subscribers.length} inscrito(s)`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AdminBimtalks;
